import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PREFERENCES_KEY = '@salon_preferences';

export type AppTheme = 'system' | 'light' | 'dark';
export type AppLanguage = 'en' | 'bn';
export type MapLook = 'light' | 'dark';
/** Which shops a customer is shown: only those that serve this gender (or both). */
export type ShopGender = 'MALE' | 'FEMALE';

/** The last place the Home screen found the customer, shown straight away on the next visit. */
export interface LastLocation {
  coords: { lat: number; lng: number };
  locationName: string;
}

interface PreferencesState {
  theme: AppTheme;
  language: AppLanguage;
  notificationsEnabled: boolean;
  bookingNotificationsEnabled: boolean;
  promotionNotificationsEnabled: boolean;
  loyaltyNotificationsEnabled: boolean;
  systemNotificationsEnabled: boolean;
  biometricEnabled: boolean;
  /** Light or night look of the shop map, kept from the last visit. */
  mapLook: MapLook;
  lastLocation: LastLocation | null;
  /** "Only show shops that serve me": on by default; off shows every shop (e.g. booking for someone else). */
  showOnlyMyGender: boolean;
  /** The filter actually applied to shop lists right now (from the profile's gender and the switch above). */
  genderFilter: ShopGender | null;
  /** The gender picked on the onboarding screen, before there was an account; saved to the account on sign-in. */
  chosenGender: 'male' | 'female' | null;
}

const initialState: PreferencesState = {
  theme: 'system',
  language: 'en',
  notificationsEnabled: true,
  bookingNotificationsEnabled: true,
  promotionNotificationsEnabled: true,
  loyaltyNotificationsEnabled: true,
  systemNotificationsEnabled: true,
  biometricEnabled: false,
  mapLook: 'light',
  lastLocation: null,
  showOnlyMyGender: true,
  genderFilter: null,
  chosenGender: null,
};

export const loadPreferences = createAsyncThunk('preferences/loadPreferences', async () => {
  try {
    const raw = await AsyncStorage.getItem(PREFERENCES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...initialState, ...parsed };
    }
  } catch (err) {
    console.error('Failed to load preferences from AsyncStorage', err);
  }
  return initialState;
});

const savePrefs = (state: PreferencesState) => {
  AsyncStorage.setItem(PREFERENCES_KEY, JSON.stringify(state)).catch((err) =>
    console.error('Failed to save preferences', err)
  );
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<AppTheme>) {
      state.theme = action.payload;
      savePrefs(state);
    },
    setLanguage(state, action: PayloadAction<AppLanguage>) {
      state.language = action.payload;
      savePrefs(state);
    },
    setNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.notificationsEnabled = action.payload;
      savePrefs(state);
    },
    setBookingNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.bookingNotificationsEnabled = action.payload;
      savePrefs(state);
    },
    setPromotionNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.promotionNotificationsEnabled = action.payload;
      savePrefs(state);
    },
    setLoyaltyNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.loyaltyNotificationsEnabled = action.payload;
      savePrefs(state);
    },
    setSystemNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.systemNotificationsEnabled = action.payload;
      savePrefs(state);
    },
    setBiometricEnabled(state, action: PayloadAction<boolean>) {
      state.biometricEnabled = action.payload;
      savePrefs(state);
    },
    setMapLook(state, action: PayloadAction<MapLook>) {
      state.mapLook = action.payload;
      savePrefs(state);
    },
    setLastLocation(state, action: PayloadAction<LastLocation>) {
      state.lastLocation = action.payload;
      savePrefs(state);
    },
    setShowOnlyMyGender(state, action: PayloadAction<boolean>) {
      state.showOnlyMyGender = action.payload;
      savePrefs(state);
    },
    setChosenGender(state, action: PayloadAction<'male' | 'female' | null>) {
      state.chosenGender = action.payload;
      savePrefs(state);
    },
    setGenderFilter(state, action: PayloadAction<ShopGender | null>) {
      state.genderFilter = action.payload;
      savePrefs(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadPreferences.fulfilled, (state, action) => {
      return { ...state, ...action.payload };
    });
  },
});

export const {
  setTheme,
  setLanguage,
  setNotificationsEnabled,
  setBookingNotificationsEnabled,
  setPromotionNotificationsEnabled,
  setLoyaltyNotificationsEnabled,
  setSystemNotificationsEnabled,
  setBiometricEnabled,
  setMapLook,
  setLastLocation,
  setShowOnlyMyGender,
  setGenderFilter,
  setChosenGender,
} = preferencesSlice.actions;
export default preferencesSlice.reducer;

